// Vietnamese-first display + parse formatters.
// Used by inputs (parse → number for form) + display (number → localized).

const VND = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })
const NUM = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 })

/** Full currency: "250.000 ₫". Use in tables, cards, summaries. */
export function money(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return VND.format(n)
}

/** Compact for narrow columns: 12.500.000 → "12,5tr", 250.000 → "250k". */
export function moneyShort(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0).replace('.', ',')}tr`
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`
  return String(n)
}

/** Plain grouped number (no currency symbol) — for money inputs where the user types digits. */
/**
 * Tỷ lệ 0–1 → phần trăm. Trả '—' khi giá trị thiếu hoặc không phải số hữu hạn.
 *
 * Có hàm này vì các chỗ viết thẳng `(data.rate * 100).toFixed(1)` đều hỏng
 * lặng lẽ: điều kiện bảo vệ thường chỉ kiểm tra object cha còn tồn tại, nên
 * khi API thiếu đúng trường đó thì `undefined * 100` ra `NaN` và người dùng
 * đọc được "NaN%" ngay trên bảng điều khiển. Kiểu TypeScript không đỡ được —
 * nó không kiểm tra dữ liệu lúc chạy.
 */
export function percent(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return `${(n * 100).toFixed(digits).replace('.', ',')}%`
}

export function num(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return ''
  return NUM.format(n)
}

function parseShorthandNumber(raw: string): number {
  const s = raw.trim()
  if (!s) return 0

  // Trường hợp có cả dấu chấm và phẩy: "1.500,5" hoặc "1,500.5"
  if (s.includes('.') && s.includes(',')) {
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0
    }
    return parseFloat(s.replace(/,/g, '')) || 0
  }

  // Trường hợp có dấu phẩy thập phân: "1,5" -> 1.5, "1,25" -> 1.25
  if (s.includes(',')) {
    const parts = s.split(',')
    const p1 = parts[1]
    if (parts.length === 2 && p1 && p1.length !== 3) {
      return parseFloat(`${parts[0]}.${p1}`) || 0
    }
    return parseFloat(s.replace(/,/g, '')) || 0
  }

  // Trường hợp có dấu chấm thập phân: "1.5" -> 1.5, "1.25" -> 1.25
  if (s.includes('.')) {
    const parts = s.split('.')
    const p1 = parts[1]
    if (parts.length === 2 && p1 && p1.length !== 3) {
      return parseFloat(`${parts[0]}.${p1}`) || 0
    }
    return parseFloat(s.replace(/\./g, '')) || 0
  }

  return parseFloat(s) || 0
}

/**
 * Parse "250.000" / "250 000" / "250,000₫" / "50k" / "1.5tr" / "1tr5" / "2 triệu" → number.
 * Hỗ trợ tiếng Việt:
 * - k, nghìn, ngàn (x1.000): 50k, 150k, 1k5 -> 1.500
 * - tr, triệu, m, củ (x1.000.000): 1tr, 1.5tr, 1tr5 -> 1.500.000, 2m, 3 củ
 * - lít, xị (x100.000): 1 lít -> 100.000, 5 xị -> 500.000
 */
export function parseMoney(input: string | null | undefined): number {
  if (!input) return 0
  let str = String(input).trim().toLowerCase()
  if (!str) return 0

  // Bỏ ký hiệu tiền tệ ₫, đ, vnd, vnđ, đồng, dong
  str = str.replace(/[₫đ]|vnd|vnđ|đồng|dong/gi, '').trim()
  if (!str) return 0

  // 1. Dạng gõ tắt có đơn vị ở giữa: "1tr5", "2tr8", "1c5" (1 triệu 500 nghìn)
  const midMillionMatch = str.match(/^([\d.,]+)\s*(?:tr|trieu|triệu|m|củ|cu)\s*(\d+)$/i)
  if (midMillionMatch && midMillionMatch[1] && midMillionMatch[2]) {
    const whole = parseShorthandNumber(midMillionMatch[1])
    const decStr = midMillionMatch[2].padEnd(6, '0').slice(0, 6)
    return Math.round(whole * 1_000_000 + parseInt(decStr, 10))
  }

  // 2. Dạng gõ tắt nghìn ở giữa: "1k5", "2k8" (1 nghìn 500 đồng)
  const midThousandMatch = str.match(/^([\d.,]+)\s*(?:k|nghìn|nghin|ngàn|ngan)\s*(\d+)$/i)
  if (midThousandMatch && midThousandMatch[1] && midThousandMatch[2]) {
    const whole = parseShorthandNumber(midThousandMatch[1])
    const decStr = midThousandMatch[2].padEnd(3, '0').slice(0, 3)
    return Math.round(whole * 1_000 + parseInt(decStr, 10))
  }

  // 3. Kiểm tra đơn vị triệu / củ / tr / m ở cuối: "1.5tr", "2m", "3 củ"
  const millionMatch = str.match(/^([\d.,]+)\s*(?:tr|trieu|triệu|m|củ|cu)$/i)
  if (millionMatch && millionMatch[1]) {
    const numPart = parseShorthandNumber(millionMatch[1])
    return Math.round(numPart * 1_000_000)
  }

  // 4. Kiểm tra đơn vị nghìn / ngàn / k ở cuối: "150k", "50 nghìn"
  const thousandMatch = str.match(/^([\d.,]+)\s*(?:k|nghìn|nghin|ngàn|ngan)$/i)
  if (thousandMatch && thousandMatch[1]) {
    const numPart = parseShorthandNumber(thousandMatch[1])
    return Math.round(numPart * 1_000)
  }

  // 5. Kiểm tra đơn vị lít / xị (1 lít / 1 xị = 100.000)
  const litMatch = str.match(/^([\d.,]+)\s*(?:lít|lit|xị|xi)$/i)
  if (litMatch && litMatch[1]) {
    const numPart = parseShorthandNumber(litMatch[1])
    return Math.round(numPart * 100_000)
  }

  // 6. Chuỗi số thông thường
  const digits = str.replace(/[^\d]/g, '')
  return digits ? Number(digits) : 0
}

// ─── Đọc số tiền thành chữ tiếng Việt ──────────────────

const VN_DIGITS = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín']

function readThreeDigits(triplet: number, isHighestTriplet: boolean): string {
  const h = Math.floor(triplet / 100)
  const t = Math.floor((triplet % 100) / 10)
  const u = triplet % 10

  if (h === 0 && t === 0 && u === 0) return ''

  const res: string[] = []

  // Hàng trăm
  if (h > 0 || !isHighestTriplet) {
    res.push(VN_DIGITS[h] + ' trăm')
  }

  // Hàng chục
  if (t === 0) {
    if (u > 0 && (h > 0 || !isHighestTriplet)) {
      res.push('lẻ')
    }
  } else if (t === 1) {
    res.push('mười')
  } else {
    res.push(VN_DIGITS[t] + ' mươi')
  }

  // Hàng đơn vị
  if (u === 1) {
    if (t > 1) {
      res.push('mốt')
    } else {
      res.push('một')
    }
  } else if (u === 4) {
    if (t > 1) {
      res.push('tư')
    } else {
      res.push('bốn')
    }
  } else if (u === 5) {
    if (t > 0) {
      res.push('lăm')
    } else {
      res.push('năm')
    }
  } else if (u > 0) {
    res.push(VN_DIGITS[u]!)
  }

  return res.join(' ')
}

/** Số gõ dạng "12,5" / "12.5" → 12.5 (kích thước, cân kg); trống / 0 / sai → null. */
export function parseDecimal(text: string): number | null {
  const t = text.trim().replace(",", ".")
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Giữ chữ số và MỘT dấu thập phân (chấm hoặc phẩy) — không nuốt dấu chấm khi đang gõ "12." */
export function sanitizeDecimalInput(raw: string, decimals = 2): string {
  const cleaned = raw.replace(/[^\d.,]/g, "").replace(",", ".")
  const [int = "", ...rest] = cleaned.split(".")
  return rest.length ? `${int}.${rest.join("").slice(0, decimals)}` : int
}

/**
 * Đọc số tiền thành chữ tiếng Việt chuẩn ngữ pháp:
 * 15.000 -> "Mười lăm nghìn đồng"
 * 150.000 -> "Một trăm năm mươi nghìn đồng"
 * 1.500.000 -> "Một triệu năm trăm nghìn đồng"
 */
export function moneyInWords(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n) || n === 0) return 'Không đồng'
  const val = Math.abs(Math.round(n))
  if (val === 0) return 'Không đồng'

  const scales = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn tỷ', 'triệu tỷ']
  const triplets: number[] = []
  let temp = val
  while (temp > 0) {
    triplets.push(temp % 1000)
    temp = Math.floor(temp / 1000)
  }

  const parts: string[] = []
  for (let i = triplets.length - 1; i >= 0; i--) {
    const trip = triplets[i]!
    if (trip > 0) {
      const isHighest = i === triplets.length - 1
      const str = readThreeDigits(trip, isHighest)
      if (str) {
        parts.push(str + (scales[i] ? ' ' + scales[i] : ''))
      }
    }
  }

  const result = parts.join(' ').replace(/\s+/g, ' ').trim()
  if (!result) return 'Không đồng'

  return result.charAt(0).toUpperCase() + result.slice(1) + ' đồng'
}

// ─── Phone ─────────────────────────────────────────────

/**
 * Chuẩn hóa số điện thoại Việt Nam từ chuỗi nhập tự do:
 * - Tự động đổi chữ 'o' / 'O' ở đầu thành số '0'.
 * - Tự động bổ sung số 0 ở đầu nếu người dùng nhập 9 số di động (vd: 912345678 -> 0912345678).
 * - Trích xuất số điện thoại 10 số nếu người dùng dán kèm chữ tiếng Việt (VD: "SĐT: 0912.345.678 (chị Lan)").
 * - Loại bỏ các ký tự dấu phân cách, dấu chấm, gạch ngang, khoảng trắng.
 */
export function normalizePhoneVN(raw: string | null | undefined): string {
  if (!raw) return ''
  let str = String(raw).trim()
  if (!str) return ''

  // 1. Tự động đổi chữ o / O ở đầu thành số 0
  str = str.replace(/^(\+?84[\s.-]*)?[oO]/i, '$10').replace(/^[oO]/, '0')

  // 2. Nếu chuỗi có chứa chữ cái hoặc văn bản kèm theo (vd: "SĐT: 0912.345.678 (gặp Lan)"),
  // trích xuất số điện thoại 10 số từ văn bản
  if (/[a-zA-Z\u00C0-\u024F\u1EA0-\u1EF9]/.test(str)) {
    const vnPhoneMatch = str.match(/(?:(?:\+?84[\s.-]*0?)|0)[35789](?:[\s.-]*\d){8}\b/)
    if (vnPhoneMatch) {
      let digits = vnPhoneMatch[0].replace(/\D/g, '')
      if (digits.startsWith('840')) digits = '0' + digits.slice(3)
      else if (digits.startsWith('84')) digits = '0' + digits.slice(2)
      if (digits.length === 10) return digits
    }
  }

  // 3. Xử lý chuẩn hóa chuỗi số thông thường
  let cleaned = str.replace(/[\s.-]/g, '')
  if (cleaned.startsWith('+840')) {
    cleaned = '0' + cleaned.slice(4)
  } else if (cleaned.startsWith('+84')) {
    cleaned = '0' + cleaned.slice(3)
  } else if (cleaned.startsWith('840') && cleaned.length >= 12) {
    cleaned = '0' + cleaned.slice(3)
  } else if (cleaned.startsWith('84') && cleaned.length >= 11) {
    cleaned = '0' + cleaned.slice(2)
  }

  // Giữ lại chỉ các chữ số
  cleaned = cleaned.replace(/\D/g, '')

  // 4. Nếu người dùng nhập 9 chữ số thuộc đầu di động (3, 5, 7, 8, 9), tự bù 0 ở đầu
  if (cleaned.length === 9 && /^[35789]/.test(cleaned)) {
    cleaned = '0' + cleaned
  }

  return cleaned
}

/**
 * Normalize VN mobile to E.164: "0912345678" or "84912345678" → "+84912345678".
 * Returns null for anything unparseable (caller can fall back to raw input).
 */
export function toE164VN(raw: string | null | undefined): string | null {
  if (!raw) return null
  const d = normalizePhoneVN(raw)
  if (!d) return null
  if (d.startsWith('0') && d.length === 10) return '+84' + d.slice(1)
  if (d.startsWith('84') && (d.length === 11 || d.length === 12)) return '+' + d
  if (d.startsWith('+84') && d.length === 12) return d
  if (d.length === 9 && /^[35789]/.test(d)) return '+84' + d
  return d.startsWith('+') ? d : '+' + d
}

/**
 * Display phone in Vietnamese-friendly grouping: "0912 345 678" from any input.
 * Falls back to the input as-is if not a VN pattern.
 */
export function phoneDisplay(raw: string | null | undefined): string {
  if (!raw) return '—'
  const norm = normalizePhoneVN(raw)
  if (norm.length === 10) {
    return `${norm.slice(0, 4)} ${norm.slice(4, 7)} ${norm.slice(7)}`
  }
  if (norm.length === 11) {
    return `${norm.slice(0, 4)} ${norm.slice(4, 7)} ${norm.slice(7)}`
  }
  return raw
}

export function isValidVnPhone(phone: string | null | undefined): boolean {
  if (!phone) return false
  const norm = normalizePhoneVN(phone)
  return /^(03|05|07|08|09)\d{8}$/.test(norm)
}

/**
 * Trả về thông báo lỗi thân thiện khi kiểm tra SĐT Việt Nam. Trả về null nếu hợp lệ.
 */
export function getPhoneValidationError(phone: string | null | undefined, required = true): string | null {
  if (!phone || !phone.trim()) {
    return required ? 'Vui lòng nhập số điện thoại' : null
  }
  const norm = normalizePhoneVN(phone)
  if (!norm) {
    return 'Số điện thoại không hợp lệ'
  }
  if (norm.length >= 2 && !/^(0[35789])/.test(norm)) {
    return 'Đầu số không hợp lệ (cần là đầu số di động VN: 03, 05, 07, 08, 09)'
  }
  if (norm.length < 10) {
    return 'Số điện thoại cần đủ 10 chữ số'
  }
  if (norm.length > 10 || !isValidVnPhone(norm)) {
    return 'Số điện thoại không đúng định dạng di động Việt Nam'
  }
  return null
}

export const formatPhoneVN = phoneDisplay

// ─── Channel → order-id prefix ─────────────────────────

export const CHANNEL_PREFIX: Record<string, string> = {
  'POS': 'POS',
  'FB Live': 'FBL',
  'Zalo': 'ZL',
  'TikTok': 'TT',
  'Shopee': 'SP',
  'Manual': 'MN',
  'Other': 'OT',
}

/** Generate a client order id keyed to channel + timestamp: "FBL-234567". */
export function makeClientOrderId(channel: string): string {
  const prefix = CHANNEL_PREFIX[channel] ?? 'OT'
  return `${prefix}-${Date.now().toString().slice(-8)}`
}

/** Swap only the prefix while preserving the sequence portion — used when user
 *  changes channel after the id was auto-generated. */
export function reprefixOrderId(currentId: string, newChannel: string): string {
  const newPrefix = CHANNEL_PREFIX[newChannel] ?? 'OT'
  const parts = currentId.split('-')
  const seq = parts.length > 1 ? parts.slice(1).join('-') : Date.now().toString().slice(-8)
  return `${newPrefix}-${seq}`
}

export interface ParsedHierarchicalOrderId {
  isHierarchical: boolean
  agencyCode: string | null
  hasCtv: boolean
  ctvCode: string | null
  shopCode: string | null
  orderSeq: string
}

/**
 * Phân tích mã đơn phân cấp (VD: DH-AG01-CT01-SP01-462569 hoặc DH-AG01-DIR-SP01-462570)
 * Giúp giao diện hiển thị nguồn đơn, đại lý và cộng tác viên trực tiếp từ mã đơn.
 */
/**
 * Mã đơn ngắn để hiện trong danh sách: "DH-AG0001-CT0001-SP0001-755176" → "DH-755176" (phần đại lý /
 * CTV / shop giống nhau ở mọi đơn của một shop). Mã không theo dạng phân cấp → giữ nguyên.
 */
export function shortOrderId(orderId: string): string {
  const p = parseHierarchicalOrderId(orderId)
  return p.isHierarchical ? `DH-${p.orderSeq}` : orderId
}

export function parseHierarchicalOrderId(orderId: string): ParsedHierarchicalOrderId {
  if (!orderId || typeof orderId !== 'string') {
    return {
      isHierarchical: false,
      agencyCode: null,
      hasCtv: false,
      ctvCode: null,
      shopCode: null,
      orderSeq: '',
    }
  }

  const parts = orderId.split('-')
  if (parts.length >= 5 && parts[0] === 'DH') {
    const agencyCode = parts[1]!
    const ctvPart = parts[2]!
    const shopCode = parts[3]!
    const orderSeq = parts.slice(4).join('-')

    const hasCtv = ctvPart !== 'DIR' && ctvPart !== '00' && ctvPart !== 'NOCTV'

    return {
      isHierarchical: true,
      agencyCode,
      hasCtv,
      ctvCode: hasCtv ? ctvPart : null,
      shopCode,
      orderSeq,
    }
  }

  return {
    isHierarchical: false,
    agencyCode: null,
    hasCtv: false,
    ctvCode: null,
    shopCode: null,
    orderSeq: orderId,
  }
}

// ─── Best Express Status Mapping ───────────────────────

export const BEST_STATUS_NAMES: Record<string, string> = {
  '211': 'Đang đi lấy hàng',
  '203': 'Lấy hàng không thành công',
  '202': 'Lấy hàng thành công',
  '301': 'Nhận hàng vào bưu cục gửi',
  '302': 'Xuất hàng đến TT chia chọn',
  '303': 'Nhận hàng tại TT chia chọn',
  '304': 'Xuất hàng khỏi TT chia chọn',
  '309': 'Nhận hàng tại bưu cục phát',
  '601': 'Đang đi giao hàng',
  '604': 'Giao hàng không thành công',
  '666': 'Giao hàng thành công',
  '605': 'Xác nhận chuyển hoàn',
  '701': 'Xuất hàng khỏi bưu cục phát để trả về',
  '702': 'Nhận hàng tại TT chia chọn để trả về',
  '703': 'Xuất hàng khỏi TT chia chọn để trả về',
  '704': 'Nhận hàng tại bưu cục trả hàng',
  '705': 'Đang đi trả hàng',
  '707': 'Trả hàng không thành công',
  '708': 'Trả hàng thành công',
  '777': 'Hàng hóa bị thất lạc',
  '1000': 'Hàng hóa bị hư hỏng',
  '2': 'Đơn đã hủy',
  'customer Cancel': 'Khách hàng hủy đơn trên Best',
}

export function formatBestStatus(code?: string | number | null, fallback?: string | null): string {
  if (code == null || code === '' || code === 'undefined') {
    return fallback || 'Cập nhật hành trình'
  }
  const key = String(code).trim()
  return BEST_STATUS_NAMES[key] || fallback || `Trạng thái: ${key}`
}

export function formatDateTime(isoOrDate?: string | Date | null): string {
  if (!isoOrDate) return '—'
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  if (isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function formatDate(isoOrDate?: string | Date | null): string {
  if (!isoOrDate) return '—'
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  if (isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d)
}

/** Chuẩn hoá tên chủ tài khoản ngân hàng: không dấu, in hoa, chuẩn VietQR/NAPAS */
export function toBankingUnaccent(str: string): string {
  if (!str) return ''
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
}


